import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Scale, ArrowLeft, Plus, FileSignature, Check, X, Pencil, Menu, Sparkles, BookOpen,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n, sopTitle, sopDesc } from "@/lib/i18n";
import LanguageToggle from "@/components/LanguageToggle";
import { LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import SopDocket from "@/components/SopDocket";
import UploadPanel from "@/components/UploadPanel";
import ItemCard from "@/components/ItemCard";
import ItemEditor from "@/components/ItemEditor";
import Chronology from "@/components/Chronology";
import ReportPreview from "@/components/ReportPreview";
import JobProgress from "@/components/JobProgress";
import { pollJob } from "@/lib/jobs";

export default function CaseWorkspace() {
  const { caseId } = useParams();
  const nav = useNavigate();
  const { logout } = useAuth();
  const { t, lang } = useI18n();
  const doLogout = async () => { await logout(); nav("/login"); };
  const [caseData, setCaseData] = useState(null);
  const [stages, setStages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [stageAnalyzing, setStageAnalyzing] = useState(null);
  const [stageAnalyses, setStageAnalyses] = useState({});

  const [editorOpen, setEditorOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [reportPhase, setReportPhase] = useState(null);

  const [titleEdit, setTitleEdit] = useState(false);
  const [titleVal, setTitleVal] = useState("");
  const [docketOpen, setDocketOpen] = useState(false);

  const loadCase = useCallback(() => api.get(`/cases/${caseId}`).then((r) => { setCaseData(r.data); setTitleVal(r.data.title); }), [caseId]);
  const loadDocs = useCallback(() => api.get(`/cases/${caseId}/documents`).then((r) => setDocuments(r.data)), [caseId]);
  const loadItems = useCallback(() => api.get(`/cases/${caseId}/items`).then((r) => setItems(r.data)), [caseId]);
  const loadStageAnalyses = useCallback(
    () => api.get(`/cases/${caseId}/stage-analyses`).then((r) => {
      const m = {};
      for (const row of r.data) m[row.stage_code] = row;
      setStageAnalyses(m);
    }),
    [caseId]
  );

  useEffect(() => {
    api.get("/sop").then((r) => setStages(r.data));
    loadCase().catch(() => { toast.error(t("ws.notFound")); nav("/dashboard"); });
    loadDocs();
    loadItems();
    loadStageAnalyses().catch(() => {});
    api.get(`/cases/${caseId}/report`).then((r) => setReport(r.data)).catch(() => setReport(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, loadCase, loadDocs, loadItems, loadStageAnalyses, nav]);

  const counts = useMemo(() => {
    const m = {};
    for (const it of items) m[it.stage_code] = (m[it.stage_code] || 0) + 1;
    return m;
  }, [items]);

  const showAnalyzeProgress = (tid, phase) =>
    toast.loading(<JobProgress kind="analyze" phase={phase} dark />, { id: tid, duration: Infinity });

  const watchAnalyze = async (jobId, tid) => {
    showAnalyzeProgress(tid, "queued");
    try {
      return await pollJob(jobId, (ph) => showAnalyzeProgress(tid, ph));
    } finally { toast.dismiss(tid); }
  };

  const finishAnalyze = async (jobId) => {
    setAnalyzing(true);
    try {
      const res = await watchAnalyze(jobId, "analyze-job");
      await loadItems();
      await loadCase();
      toast.success(t("ws.extracted", { count: res.count }));
    } catch (e) { toast.error(e.message || apiErr(e)); }
    finally { setAnalyzing(false); }
  };

  const analyze = async () => {
    try {
      const { data } = await api.post(`/cases/${caseId}/analyze`, { language: lang });
      await finishAnalyze(data.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const finishStage = async (jobId, code) => {
    setStageAnalyzing(code);
    try {
      const res = await watchAnalyze(jobId, `stage-job-${code}`);
      await loadItems();
      await loadStageAnalyses();
      await loadCase();
      toast.success(t("ws.stageDone", { code, count: res.count }));
    } catch (e) { toast.error(e.message || apiErr(e)); }
    finally { setStageAnalyzing(null); }
  };

  const analyzeStage = async (code) => {
    try {
      const { data } = await api.post(`/cases/${caseId}/stages/${code}/analyze`, { language: lang });
      await finishStage(data.id, code);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const finishReport = async (jobId) => {
    setGenerating(true);
    setReportPhase("queued");
    try {
      const res = await pollJob(jobId, setReportPhase);
      const r = await api.get(`/cases/${caseId}/report`);
      setReport(r.data);
      await loadCase();
      await loadDocs();
      toast.success(t("ws.reportDone"));
      if (res.documents_purged) {
        toast.success(t("report.purgedToast", { count: res.documents_purged_count }), { duration: 6000 });
      }
    } catch (e) { toast.error(e.message || apiErr(e)); }
    finally { setGenerating(false); setReportPhase(null); }
  };

  const generate = async () => {
    try {
      const { data } = await api.post(`/cases/${caseId}/report`, { language: lang });
      await finishReport(data.id);
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Resume progress for jobs still running (e.g. after a page refresh).
  useEffect(() => {
    api.get(`/cases/${caseId}/jobs`).then(({ data }) => {
      for (const j of data) {
        if (j.kind === "analyze") finishAnalyze(j.id);
        else if (j.kind === "report") { setReportOpen(true); finishReport(j.id); }
        else if (j.kind.startsWith("stage:")) finishStage(j.id, j.kind.slice(6));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  const openReport = () => {
    setReportOpen(true);
    if (!report && items.length > 0 && !generating) generate();
  };

  const deleteItem = async (it) => {
    try { await api.delete(`/cases/${caseId}/items/${it.id}`); loadItems(); toast.success(t("ws.itemDeleted")); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openEditor = (it) => { setEditItem(it); setEditorOpen(true); };
  const openAdd = () => { setEditItem(null); setEditorOpen(true); };

  const saveTitle = async () => {
    try { await api.patch(`/cases/${caseId}`, { title: titleVal }); setTitleEdit(false); loadCase(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const selStage = stages.find((s) => s.code === selected);
  const isChron = selected === "LCA-008";
  const isStagePanel = !!selStage && selStage.code !== "LCA-015";
  const stageDocs = selected ? documents.filter((d) => d.stage_code === selected) : [];
  const stageItems = selected ? items.filter((i) => i.stage_code === selected) : [];
  const summaryCodes = Object.keys(stageAnalyses);

  if (!caseData) return <div className="min-h-screen grid place-items-center text-zinc-600 font-mono-ui text-sm">{t("common.loading")}</div>;

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      {/* Top bar */}
      <header className="shrink-0 backdrop-blur-xl bg-[#0a0a0c]/80 border-b border-white/5">
        <div className="px-4 sm:px-6 h-16 flex items-center gap-4">
          <button data-testid="back-btn" onClick={() => nav("/dashboard")} className="text-zinc-500 hover:text-zinc-200 transition-colors">
            <ArrowLeft className="h-5 w-5" />
          </button>
          {/* Mobile SOP docket drawer */}
          <Sheet open={docketOpen} onOpenChange={setDocketOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-docket-btn"
                className="md:hidden text-zinc-500 hover:text-zinc-200 transition-colors">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 bg-[#121216] border-zinc-800">
              <SheetTitle className="sr-only">SOP Analisis</SheetTitle>
              <SopDocket stages={stages} counts={counts} selected={selected}
                onSelect={(code) => { setSelected(code); setDocketOpen(false); }}
                analyzed={caseData.status !== "draft"} summaries={summaryCodes} />
            </SheetContent>
          </Sheet>
          <Scale className="hidden sm:block h-4 w-4 text-[#d4af37]" strokeWidth={1.5} />
          <div className="flex-1 min-w-0">
            {titleEdit ? (
              <div className="flex items-center gap-2 max-w-lg">
                <Input data-testid="rename-input" value={titleVal} onChange={(e) => setTitleVal(e.target.value)}
                  className="h-9 bg-[#0a0a0c] border-zinc-800" />
                <button onClick={saveTitle} className="text-emerald-400 hover:text-emerald-300"><Check className="h-4 w-4" /></button>
                <button onClick={() => { setTitleEdit(false); setTitleVal(caseData.title); }} className="text-zinc-500 hover:text-zinc-300"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <button data-testid="case-title" onClick={() => setTitleEdit(true)}
                className="group flex items-center gap-2 max-w-full">
                <span className="truncate font-serif-display text-lg text-zinc-100">{caseData.title}</span>
                <Pencil className="h-3.5 w-3.5 text-zinc-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}
          </div>
          <span className="hidden md:block font-mono-ui text-[11px] text-zinc-600">
            {t("ws.counts", { items: items.length, docs: documents.length })}
          </span>
          <LanguageToggle />
          <a data-testid="workspace-manual-link"
            href={`${process.env.REACT_APP_BACKEND_URL}/api/manual.pdf`}
            target="_blank" rel="noopener noreferrer" title={t("common.manualDownload")}
            className="text-zinc-500 hover:text-zinc-200 transition-colors">
            <BookOpen className="h-4 w-4" />
          </a>
          <button data-testid="logout-btn" onClick={doLogout} title={t("common.logout")}
            className="ml-1 text-zinc-500 hover:text-rose-300 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Docket sidebar */}
        <aside className="hidden md:block w-72 shrink-0 border-r border-zinc-800 bg-[#121216]">
          <SopDocket stages={stages} counts={counts} selected={selected} onSelect={setSelected}
            analyzed={caseData.status !== "draft"} summaries={summaryCodes} />
        </aside>

        {/* Main workspace */}
        <main className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-4xl px-5 sm:px-8 py-8 pb-28">
            {isStagePanel ? (
              <UploadPanel key={selected} caseId={caseId} documents={stageDocs} onChange={loadDocs}
                onAnalyze={() => analyzeStage(selected)} analyzing={stageAnalyzing === selected}
                stageCode={selected} title={t("ws.filesStage", { code: selStage.code })} analyzeLabel={t("ws.analyzeStage")} />
            ) : (
              <UploadPanel caseId={caseId} documents={documents} onChange={loadDocs}
                onAnalyze={analyze} analyzing={analyzing} title={t("ws.allFiles")} />
            )}

            {/* Section header */}
            <div className="mt-10 flex items-end justify-between gap-4 flex-wrap">
              <div>
                <p className="font-mono-ui text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                  {selStage ? selStage.code : t("ws.allStages")}
                </p>
                <h2 className="mt-1.5 font-serif-display text-2xl sm:text-3xl font-light text-zinc-50">
                  {selStage ? sopTitle(t, selStage) : t("ws.analysisItems")}
                </h2>
                {selStage && <p className="mt-1 text-sm text-zinc-500">{sopDesc(t, selStage)}</p>}
              </div>
              <Button data-testid="add-item-btn" onClick={openAdd} variant="outline"
                className="h-9 border-zinc-700 text-zinc-200 hover:bg-zinc-800 rounded-full">
                <Plus className="h-4 w-4 mr-1.5" /> {t("ws.addItem")}
              </Button>
            </div>

            {/* AI stage summary */}
            {isStagePanel && stageAnalyses[selected]?.summary && (
              <div data-testid="stage-summary" className="mt-6 rounded-xl border border-[#d4af37]/25 bg-[#d4af37]/[0.06] p-5">
                <p className="flex items-center gap-2 font-mono-ui text-[10px] uppercase tracking-wider text-[#d4af37]">
                  <Sparkles className="h-3.5 w-3.5" /> {t("ws.aiSummary")} · {selStage.code}
                </p>
                <p className="mt-2 text-sm leading-relaxed text-zinc-200">{stageAnalyses[selected].summary}</p>
              </div>
            )}

            {/* Body */}
            <div className="mt-6">
              {selStage && selStage.code === "LCA-015" ? (
                <div className="rounded-xl border border-dashed border-zinc-800 p-10 text-center">
                  <FileSignature className="mx-auto h-6 w-6 text-zinc-700" strokeWidth={1.5} />
                  <p className="mt-3 text-sm text-zinc-500 max-w-md mx-auto">
                    {t("ws.finalStageText")}
                  </p>
                </div>
              ) : (
                <>
                  {(isStagePanel ? stageItems : items).length === 0 ? (
                    <div className="rounded-xl border border-dashed border-zinc-800 p-12 text-center">
                      <FileSignature className="mx-auto h-7 w-7 text-zinc-700" strokeWidth={1.5} />
                      <p className="mt-3 text-zinc-500 text-sm">
                        {t("ws.emptyItems")}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {(isStagePanel ? stageItems : items).map((it) => (
                        <ItemCard key={it.id} item={it} onEdit={openEditor} onDelete={deleteItem} />
                      ))}
                    </div>
                  )}

                  {isChron && (
                    <div className="mt-8">
                      <Chronology items={items} />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Persistent hero CTA */}
      <button
        data-testid="btn-generate-report"
        onClick={openReport}
        className="cta-glow fixed bottom-6 right-6 z-50 rounded-full px-6 py-3.5 bg-zinc-50 text-zinc-950 font-medium tracking-tight hover:scale-105 active:scale-[0.98] transition-transform duration-200 flex items-center gap-2"
      >
        <FileSignature className="h-4 w-4" />
        {t("ws.genReport")}
      </button>

      <ItemEditor caseId={caseId} item={editItem} open={editorOpen} onOpenChange={setEditorOpen}
        documents={documents} onSaved={loadItems} />
      <ReportPreview open={reportOpen} onOpenChange={setReportOpen} report={report}
        generating={generating} phase={reportPhase} onGenerate={generate} caseId={caseId} />
    </div>
  );
}
